import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Award, MapPin, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.05 } }),
};

function MemberCard({ m, idx }) {
  return (
    <motion.div
      variants={fadeUp}
      custom={idx}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      className="founding-card relative border border-white/10 bg-[#121214]"
      data-testid="founding-member-card"
    >
      <Link to={`/founding/${m.id}`} className="block p-6" data-testid={`founding-member-link-${m.id}`}>
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-[#dc2626]/15 border border-[#dc2626]/40 seat-pulse">
          <Sparkles size={9} className="text-[#dc2626]" />
          <span className="font-mono-apex text-[8px] uppercase tracking-[0.25em] text-[#fca5a5]">
            {m.seat_number ? `seat #${String(m.seat_number).padStart(3, "0")}` : "founding"}
          </span>
        </div>
        <Award size={20} className="text-[#dc2626] mb-4" strokeWidth={1.5} />
        <div className="font-display text-2xl text-white tracking-tight leading-tight mb-2">{m.company}</div>
        <div className="font-mono-apex text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4 flex items-center gap-1.5">
          <MapPin size={10} /> {m.city ? `${m.city}, ${m.state}` : m.state} · {m.primary_zip}
        </div>
        {m.services?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {m.services.slice(0, 4).map((s) => (
              <span key={s} className="px-2 py-0.5 border border-white/10 font-mono-apex text-[9px] uppercase tracking-[0.15em] text-zinc-300">
                {s}
              </span>
            ))}
          </div>
        )}
        {m.certifications && (
          <div className="flex items-center gap-2 font-mono-apex text-[10px] text-zinc-400">
            <ShieldCheck size={11} className="text-[#dc2626]" /> {m.certifications}
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between font-mono-apex text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          <span>{m.crews_count} crew{m.crews_count === 1 ? "" : "s"}</span>
          <span>{m.years_active}y experience</span>
        </div>
        <div className="mt-4 flex items-center gap-1.5 font-mono-apex text-[10px] uppercase tracking-[0.2em] text-zinc-400 opacity-0 group-hover:opacity-100 transition-opacity">
          View seat <ArrowRight size={11} />
        </div>
      </Link>
    </motion.div>
  );
}

export default function FoundingMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/founding-members/public");
        setMembers(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section
      id="founding-members"
      className="relative py-24 md:py-32 border-t border-white/5"
      data-testid="founding-members-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="block w-8 h-px bg-[#dc2626]" />
          <span className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-400">
            the wall · founding companies
          </span>
        </div>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
          <h2 className="font-display text-5xl md:text-6xl text-white tracking-tight max-w-3xl leading-[0.95]">
            <span className="silver-text italic">{members.length}</span> of <span className="silver-text">100</span> founding seats claimed.
          </h2>
          <p className="font-body text-base text-zinc-400 max-w-md">
            The first restoration companies in America to wire their operations into the Apex
            network. Lifetime founding badge. Permanent placement on this wall.
          </p>
        </div>

        {!loading && members.length === 0 && (
          <div className="border border-white/10 bg-[#121214] p-12 md:p-16 text-center" data-testid="founding-members-empty">
            <Sparkles size={28} className="text-[#dc2626] mx-auto mb-6" strokeWidth={1.5} />
            <div className="font-display text-3xl md:text-4xl text-white tracking-tight mb-4">
              The wall is waiting for its first name.
            </div>
            <p className="font-body text-base text-zinc-400 leading-relaxed max-w-2xl mx-auto mb-8">
              The first 100 restoration companies in America to join Apex are listed here — visible
              to every homeowner, every adjuster, every storm. Be the first.
            </p>
            <a
              href="#join-network"
              className="inline-flex items-center gap-2 btn-primary-apex px-7 py-4 font-mono-apex text-[11px] uppercase tracking-[0.25em]"
              data-testid="founding-members-cta"
            >
              Apply for seat #1 <ArrowRight size={14} />
            </a>
          </div>
        )}

        {members.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {members.map((m, i) => (
                <MemberCard key={m.id} m={m} idx={i} />
              ))}
            </div>
            {members.length < 100 && (
              <div className="mt-10 text-center">
                <a
                  href="#join-network"
                  className="inline-flex items-center gap-2 btn-secondary-apex px-7 py-4 font-mono-apex text-[10px] uppercase tracking-[0.25em]"
                  data-testid="founding-members-cta"
                >
                  Claim seat #{members.length + 1} <ArrowRight size={14} />
                </a>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
